import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import * as urlJoin from "url-join";
import { Props } from "wasabi-common";
import Objects from "wasabi-common/lib/types/Objects";
import Strings from "wasabi-common/lib/types/Strings";

import { Exception } from "../error/Errors";
import sessionStore from "../store/sessionStore";
import { HttpStatus } from "./HttpStatus";
import language from "./language/language";

export interface RequestProps extends AxiosRequestConfig {
  name?: string;
  url?: string;
  read?: AxiosRequestConfig;
  create?: AxiosRequestConfig;
  update?: AxiosRequestConfig;
  delete?: AxiosRequestConfig;
  secured?: boolean;
}

export interface RequestResponse<E = any> extends AxiosResponse {
  data: {
    data: E;
    code?: string;
    result?: "ok" | "fail";
    status?: number;
  };
}

export default class Request {
  protected props: RequestProps;
  protected instance: AxiosInstance;

  public constructor(props: RequestProps) {
    this.instance = axios.create();
    const transport = {
      read: {
        method: "GET",
        url: props.url,
      },
      create: {
        method: "POST",
        url: props.url,
      },
      delete: {
        method: "DELETE",
        url: props.url,
      },
      update: {
        method: "PUT",
        url: props.url,
      },
    };

    this.props = Objects.mergeDefaults(transport, props);
    this.props.secured = this.props.secured !== false;
    this.props.name = this.props.name || this.constructor.name;
  }

  public static setup(options: RequestProps) {
    Objects.mergeDefaults(options, axios.defaults);
  }

  public static getConfig(defaults: AxiosRequestConfig, data?: any, ...urls: string[]): AxiosRequestConfig {
    const config = Objects.clone(defaults) || {};
    if (!config.headers) {
      config.headers = {};
    }
    config.headers.crossDomain = true;
    if (config.withCredentials !== false) {
      const token = sessionStore.getToken();
      if (token) {
        config.headers[sessionStore.TOKEN_KEY] = token;
      }
      const path = sessionStore.getPath();
      config.url = urlJoin("", path, config.url, ...urls);
    } else {
      config.url = urlJoin("", config.url, ...urls);
    }
    config.data = data;
    return config;
  }

  public fetch(query?: string): Promise<any> {
    this.log("fetch");
    return this.request({ withCredentials: this.props.secured, ...this.props.read }, undefined, query);
  }

  public findById(id: string): Promise<any> {
    this.log("findById", id);
    return this.request(this.props.read, undefined, id);
  }

  public create(item: Props<any>): Promise<any> {
    return this.request(this.props.create, item);
  }

  public update(id: string, item: Props<any>): Promise<any> {
    return this.request(this.props.update, item, id);
  }

  public delete(id: string, item: Props<any>): Promise<any> {
    return this.request(this.props.delete, item, id);
  }

  public request(config: AxiosRequestConfig, item?: Props<any>, id?: string) {
    return this.instance
      .request(Request.getConfig(config, item, id))
      .then((response: RequestResponse<any[]>) => response.data.data)
      .catch(Request.catchHttpError);
  }

  protected log(f: string, data?: any, id?: any) {
    // console.log(`${this.props.name} => ${f}`, data ? data : '', id ? id : '');
  }

  protected getUrl(url?: string) {
    if (
      Strings.startsWith(url, "http:") ||
      Strings.startsWith(url, "https:") ||
      Strings.startsWith(url, "http:") ||
      Strings.startsWith(url, "www.")
    ) {
      return url;
    }
    const secured = this.props.secured ? sessionStore.getPath() : "";
    if (url) {
      return urlJoin(secured, this.props.url, url);
    }
    return urlJoin(secured, this.props.url);
  }

  public static catchHttpError(result: any) {
    if (result) {
      let data = result;
      if (data.response) data = data.response;
      if (data.data) data = data.data;
      if (!result.response) {
        if (data.message === "Network Error") {
          data = {
            status: 0,
            code: "NETWORK_UNREACHABLE",
            description: data.message,
          };
        }
      }
      if (data.data) {
        data = data.data;
      }
      const { status, statusText, code, description, details } = data;
      let moreInfo = "";
      if (details && Array.isArray(details)) {
        moreInfo = details.join("  ");
      }

      const message = moreInfo || language.value(status, code) || description || statusText || result?.message;
      throw new Exception(status, code, message);
    }
    throw new Exception(HttpStatus.NO_CONTENT, null, "No Content");
  }

  public getProps() {
    return this.props;
  }
}

export { AxiosRequestConfig };
